import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { useCreditsStore } from './creditsStore';
import { authHeaders } from '../lib/apiAuth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai`;

function _getUserId() {
  try {
    return useAuthStore.getState().user?.id || null;
  } catch { return null; }
}

export const useYenomAIStore = create((set, get) => ({
  conversations: [],
  conversationsLoading: false,
  activeConversationId: null,
  messages: [],
  streaming: false,
  error: null,
  toolStatus: [],

  fetchConversations: async () => {
    set({ conversationsLoading: true });
    try {
      const uid = _getUserId();
      const params = new URLSearchParams({ page_size: '50' });
      if (uid) params.set('user_id', uid);
      const res = await fetch(`${API}/conversations?${params}`, { headers: { ...authHeaders() } });
      const data = await res.json();
      set({ conversations: data || [], conversationsLoading: false });
    } catch {
      set({ conversationsLoading: false });
    }
  },

  loadConversation: async (convId) => {
    set({ activeConversationId: convId, messages: [], error: null });
    try {
      const res = await fetch(`${API}/conversations/${convId}`, { headers: { ...authHeaders() } });
      const data = await res.json();
      const msgs = (data.messages || [])
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content, id: m.id }));
      set({ messages: msgs, activeConversationId: convId });
    } catch (err) {
      set({ error: 'Failed to load conversation' });
    }
  },

  deleteConversation: async (convId) => {
    try {
      await fetch(`${API}/conversations/${convId}`, { method: 'DELETE', headers: { ...authHeaders() } });
      set(s => ({
        conversations: s.conversations.filter(c => c.id !== convId),
        ...(s.activeConversationId === convId ? { activeConversationId: null, messages: [] } : {}),
      }));
    } catch { /* ignore */ }
  },

  sendMessage: async (message, context) => {
    const { activeConversationId, messages } = get();

    const userMsg = { role: 'user', content: message, id: `tmp-${Date.now()}` };
    set({
      messages: [...messages, userMsg],
      streaming: true,
      error: null,
      toolStatus: [],
    });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 200_000);

      const res = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          message,
          conversation_id: activeConversationId || undefined,
          context: context || undefined,
          user_id: _getUserId() || undefined,
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        clearTimeout(timeoutId);
        let errMsg = `Server error (${res.status})`;
        let errDetail = null;
        try {
          const errData = await res.json();
          errDetail = errData.detail;
          errMsg = (errDetail && typeof errDetail === 'object' ? errDetail.message : errDetail)
            || errData.message
            || errMsg;
        } catch { /* ignore parse error */ }

        // 402 insufficient_credits — open the top-up modal instead of
        // showing a red error banner, so users can recover in one click.
        if (res.status === 402 && errDetail && errDetail.error === 'insufficient_credits') {
          const cs = useCreditsStore.getState();
          cs.setBalance(errDetail.balance);
          cs.openTopup({
            action: errDetail.action,
            cost: errDetail.cost,
            balance: errDetail.balance,
          });
          set(s => ({
            messages: s.messages.filter(m => !(m.role === 'assistant' && (!m.content || !m.content.trim()))),
            streaming: false,
            error: null,
            toolStatus: [],
          }));
          return;
        }

        set(s => ({
          messages: s.messages.filter(m => !(m.role === 'assistant' && (!m.content || !m.content.trim()))),
          streaming: false,
          error: errMsg,
          toolStatus: [],
        }));
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let aiText = '';
      let convId = activeConversationId;
      let lineBuf = '';

      const assistantMsg = { role: 'assistant', content: '', id: `ai-${Date.now()}` };
      set(s => ({ messages: [...s.messages, assistantMsg] }));

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        lineBuf += decoder.decode(value, { stream: true });
        const parts = lineBuf.split('\n');
        lineBuf = parts.pop() || '';

        const lines = parts.filter(l => l.startsWith('data: '));

        for (const line of lines) {
          const raw = line.slice(6);
          if (raw === '[DONE]') continue;

          try {
            const evt = JSON.parse(raw);

            if (evt.type === 'conversation_id') {
              convId = evt.id;
              if (!activeConversationId) {
                set({ activeConversationId: convId });
              }
            } else if (evt.type === 'credits') {
              useCreditsStore.getState().setBalance(evt.balance);
            } else if (evt.type === 'text') {
              aiText += evt.content || '';
              set(s => {
                const msgs = [...s.messages];
                const last = msgs[msgs.length - 1];
                if (last && last.role === 'assistant') {
                  msgs[msgs.length - 1] = { ...last, content: aiText };
                }
                return { messages: msgs };
              });
            } else if (evt.type === 'tool_use') {
              set(s => {
                const ts = [...s.toolStatus];
                const idx = ts.findIndex(t => t.name === evt.name);
                if (idx >= 0) {
                  ts[idx] = { ...ts[idx], status: evt.status };
                } else {
                  ts.push({ name: evt.name, status: evt.status });
                }
                return { toolStatus: ts };
              });
            } else if (evt.type === 'title') {
              set(s => ({
                conversations: [
                  { id: convId, title: evt.title, created_at: new Date().toISOString(), updated_at: new Date().toISOString(), preview: message.slice(0, 120) },
                  ...s.conversations.filter(c => c.id !== convId),
                ],
              }));
            } else if (evt.type === 'error') {
              set({ error: evt.message });
            }
          } catch { /* skip malformed lines */ }
        }
      }

      clearTimeout(timeoutId);

      set(s => {
        const msgs = s.messages.filter(m => !(m.role === 'assistant' && (!m.content || !m.content.trim())));
        const gotResponse = msgs.some(m => m.role === 'assistant' && m.content?.trim());
        return {
          messages: msgs,
          streaming: false,
          toolStatus: [],
          error: !gotResponse && !s.error ? 'No response received. The query may have timed out — try a simpler question.' : s.error,
        };
      });

      if (convId && !activeConversationId) {
        get().fetchConversations();
      }
    } catch (err) {
      const isAbort = err.name === 'AbortError';
      const isSafariLoad = err.message === 'Load failed';
      const errMsg = isAbort
        ? 'Request timed out. Complex queries may take a while — try again or simplify the question.'
        : isSafariLoad
          ? 'Connection to the server was lost. Please check your internet connection and try again.'
          : (err.message || 'Connection lost. Please try again.');
      set(s => ({
        messages: s.messages.filter(m => !(m.role === 'assistant' && (!m.content || !m.content.trim()))),
        streaming: false,
        error: errMsg,
        toolStatus: [],
      }));
    }
  },

  newConversation: () => {
    set({ activeConversationId: null, messages: [], error: null, toolStatus: [] });
  },

  memo: null,
  memoLoading: false,
  memoError: null,

  generateMemo: async (convId, memoType = 'ic_memo') => {
    set({ memoLoading: true, memoError: null, memo: null });
    try {
      const res = await fetch(`${API}/conversations/${convId}/memo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ memo_type: memoType }),
      });
      if (!res.ok) {
        let errMsg = `Server error (${res.status})`;
        try {
          const errData = await res.json();
          errMsg = errData.detail || errData.message || errMsg;
        } catch { /* ignore */ }
        set({ memoLoading: false, memoError: errMsg });
        return;
      }
      const data = await res.json();
      set({ memo: data, memoLoading: false });
    } catch (err) {
      set({ memoLoading: false, memoError: err.message || 'Failed to generate memo' });
    }
  },

  clearMemo: () => set({ memo: null, memoLoading: false, memoError: null }),

  signals: [],
  signalsLoading: false,

  fetchSignals: async (signalType) => {
    set({ signalsLoading: true });
    try {
      const params = new URLSearchParams({ page_size: '50' });
      if (signalType) params.set('signal_type', signalType);
      const res = await fetch(`${API}/signals?${params}`, { headers: { ...authHeaders() } });
      const data = await res.json();
      set({ signals: data || [], signalsLoading: false });
    } catch {
      set({ signalsLoading: false });
    }
  },

  runScan: async () => {
    try {
      await fetch(`${API}/scan`, { method: 'POST', headers: { ...authHeaders() } });
      get().fetchSignals();
    } catch { /* ignore */ }
  },

  reset: () => {
    set({
      activeConversationId: null,
      messages: [],
      streaming: false,
      error: null,
      toolStatus: [],
    });
  },
}));
